/**
 * Pure derivation behind the capabilities modal: which filter chips to offer,
 * and which tools survive the current use-case / entity / search selection.
 *
 * No React — this is the testable half of the modal.
 */

import { ALL_KEY, ENTITY_ORDER, USE_CASE_ORDER } from './labels';
import type { ChatToolCapabilityItem } from './types';

/** Use-case chips to offer: those present in the data, in canonical order. */
export function availableUseCaseGroups(tools: readonly ChatToolCapabilityItem[]): string[] {
  const present = new Set<string>();
  tools.forEach((t) => t.useCaseGroups?.forEach((g) => present.add(g)));
  return USE_CASE_ORDER.filter((g) => present.has(g));
}

/** Entity chips to offer: those present in the data, in canonical order. */
export function availableEntities(tools: readonly ChatToolCapabilityItem[]): string[] {
  const present = new Set<string>();
  tools.forEach((t) => t.entities?.forEach((e) => present.add(e)));
  return ENTITY_ORDER.filter((e) => present.has(e));
}

/** Everything a free-text search matches against, for one tool. */
function searchableText(tool: ChatToolCapabilityItem): string {
  return [
    tool.displayName,
    tool.name,
    tool.useCase,
    tool.category,
    tool.examplePrompt || '',
    ...(tool.examplePrompts || []),
    ...(tool.entities || []),
    ...(tool.useCaseGroups || []),
  ]
    .join(' ')
    .toLowerCase();
}

export interface CapabilityFilterState {
  useCase: string;
  entity: string;
  search: string;
}

/**
 * The two chip axes AND together (not union), and the search narrows further —
 * picking Marketing + Ticket when no tool is both is an empty list, by design.
 */
export function filterCapabilities(
  tools: readonly ChatToolCapabilityItem[],
  { useCase, entity, search }: CapabilityFilterState
): ChatToolCapabilityItem[] {
  const query = search.toLowerCase().trim();
  return tools.filter((t) => {
    const matchUseCase = useCase === ALL_KEY || (t.useCaseGroups && t.useCaseGroups.includes(useCase));
    const matchEntity = entity === ALL_KEY || (t.entities && t.entities.includes(entity));
    if (query) {
      return matchUseCase && matchEntity && searchableText(t).includes(query);
    }
    return matchUseCase && matchEntity;
  });
}

/** Extra example phrasings shown under Try, deduped against the primary one. */
export const MAX_EXTRA_EXAMPLE_PROMPTS = 2;

export function extraExamplePrompts(tool: ChatToolCapabilityItem): string[] {
  return (tool.examplePrompts ?? [])
    .filter((p) => p !== tool.examplePrompt)
    .slice(0, MAX_EXTRA_EXAMPLE_PROMPTS);
}
